import * as React from 'react';
import {flow, omit} from 'lodash';
import {DragSource, DropTarget} from 'react-dnd';

import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {
  emotionStyled,
  styled,
  styledDiv,
  useTheme,
  tokens,
  type Theme,
} from '@packages/utilities/styled';
import {
  unselectable,
  oneLineText,
} from '@packages/systems/design-system/constants/Styles';

import {
  IconButton,
  PropListDraggerIcon,
  AddIcon,
  DeleteIcon,
  HideIcon,
  ShowIcon,
  EditIcon,
  DuplicateOutlineIcon,
  WarningTriangleIcon,
  Tooltip,
  Radio,
  Checkbox,
  View,
  Icon as SpringIcon,
  TargetIcon,
} from '@packages/systems/spring';

import {useTranslation} from '@packages/systems/designer-translations';
import type {DesignerTranslationFn} from '@packages/systems/designer-translations';

import type {CSSProperties} from '@packages/utilities/css-properties-type';

const iconStyler = ({
  theme,
  useLargeIcons,
}: {
  theme: Theme;
  useLargeIcons: boolean;
}) =>
  !useLargeIcons
    ? {
        width: theme.gutters.x12,
        height: theme.gutters.x12,
        maxHeight: theme.gutters.x12,
      }
    : {};

const IconWrapper = styledDiv(
  ({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.gutters.x4,
  }),
  {
    noGap: {
      marginRight: 0,
    },
  }
);

const GrabItemIcon = styled(PropListDraggerIcon)(
  ({theme: {colors, gutters}}) => ({
    color: colors.foregroundDimmer,
    cursor: 'grab',
    width: gutters.x12,
  })
);
// Using `is` prefix here to not conflict with HTML hidden attribute
const IconContainer = styled('span')((props) => ({
  ...unselectable,
  // @ts-expect-error - TS2345 - Argument of type 'PropsWithTheme<PropsT>' is not assignable to parameter of type '{ theme: any; useLargeIcons: any; }'.
  ...iconStyler(props),
  gridColumnStart: 2,
  opacity: props.isHidden ? 0.5 : 1,
  alignContent: 'center',
}));

const LabelContainer = styled('span')(({isHidden, columnNumber}) => ({
  ...oneLineText,
  ...unselectable,
  minWidth: 0,
  gridColumnStart: columnNumber,
  opacity: isHidden ? 0.5 : 1,
}));

type PropertyListIconButtonProps = {
  'aria-label'?: string;
  'data-automation-id'?: string;
  tooltip: string | React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  style?: Partial<React.CSSProperties>;
  onClick: (e: React.SyntheticEvent<HTMLElement>) => void;
  onMouseEnter?: (e: React.SyntheticEvent<HTMLElement>) => void;
  onMouseLeave?: (e: React.SyntheticEvent<HTMLElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  _ref?: React.RefObject<any> | ((value: any) => void);
};

type PropertyListIconAddButtonProps = PropertyListIconButtonProps & {
  forceTooltip?: boolean;
  density?: React.ComponentProps<typeof IconButton>['density'];
};

const UnlinkedWarningTooltipTopText = styledDiv(({theme: {gutters}}) => ({
  marginBottom: gutters.x8,
}));

export const TransparentPropertyListIconButton = ({
  'aria-label': ariaLabel,
  disabled,
  icon,
  tooltip,
  ...props
}: PropertyListIconButtonProps) => {
  return (
    <TooltipTrigger content={tooltip} alternate>
      <IconButton
        {...props}
        isDisabled={disabled}
        // Improve integration with Spring's IconButton.
        Icon={icon as any}
        label={(ariaLabel || tooltip) as string}
        variant="ghost"
      />
    </TooltipTrigger>
  );
};

const lastColumn: Partial<React.CSSProperties> = {
  justifySelf: 'end',
  gridColumnEnd: -1,
};

export const PropertyListAddButton = ({
  disabled,
  // Improve integration with Spring's IconButton.
  // eslint-disable-next-line unused-imports/no-unused-vars, unused-imports/no-unused-vars
  icon,
  forceTooltip,
  onKeyDown,
  tooltip,
  _ref,
  ...props
}: PropertyListIconAddButtonProps) => {
  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event && event.key === 'Enter') {
      event.stopPropagation();
    }
    onKeyDown && onKeyDown(event);
  };

  const button = (
    <IconButton
      ref={_ref}
      aria-disabled={disabled}
      isDisabled={disabled}
      // @ts-expect-error - TS2783 - 'onClick' is specified more than once, so this usage will be overwritten.
      onClick={props.onClick}
      onKeyDown={handleOnKeyDown}
      role="button"
      style={lastColumn}
      type="button"
      {...props}
      Icon={AddIcon}
      // Improve integration with Spring's IconButton.
      label={tooltip as string}
      variant="ghost"
    />
  );

  if (disabled) {
    return forceTooltip ? (
      <TooltipTrigger content={tooltip} alternate>
        {button}
      </TooltipTrigger>
    ) : (
      button
    );
  } else {
    return (
      <TooltipTrigger content={tooltip} alternate>
        {button}
      </TooltipTrigger>
    );
  }
};

const getSortType = ({sortType}: EnhancedItemProps) => sortType;

const makeSortable = flow(
  DragSource(
    getSortType,
    {
      /**
       * Called when dragging starts; must return the only relevant data about the item
       * being dragged:
       */
      beginDrag: ({id}: EnhancedItemProps) => ({id}),

      /**
       * Triggered when dragging ends:
       */
      endDrag: ({id, onItemDropped}: EnhancedItemProps, monitor) => {
        if (onItemDropped && monitor.didDrop()) {
          onItemDropped({
            itemId: id,
            dropTargetId: monitor.getDropResult().id,
          });
        }
      },
    },
    (connect, monitor) => ({
      isDragging: monitor.isDragging(),
      connectDragSource: connect.dragSource(),
    })
  ),
  DropTarget(
    getSortType,
    {
      drop: ({id}) => ({id}),
    },
    (connect, monitor) => {
      const item = monitor.getItem();
      return {
        idOfItemBeingDragged: item ? item.id : undefined,
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        connectDropTarget: connect.dropTarget(),
      };
    }
  )
);

type DropIndicatorProps = {
  theme: Theme;
  id: string;
  idOfItemBeingDragged?: string;
  order: Array<string>;
  'data-automation-id'?: string;
};

export const DropIndicator = ({
  theme,
  id,
  idOfItemBeingDragged,
  order,
  'data-automation-id': dataAutomationId,
}: DropIndicatorProps) => {
  if (!idOfItemBeingDragged) {
    return null;
  }

  const itemBeingDraggedIsAbove =
    order.indexOf(id) > order.indexOf(idOfItemBeingDragged);
  return (
    <div
      data-automation-id={dataAutomationId}
      style={{
        position: 'absolute',
        ...(itemBeingDraggedIsAbove ? {bottom: -1} : {top: -1}),
        width: '100%',
        backgroundColor: theme.colors.accentVivid,
        height: 3,
        borderRadius: 3,
      }}
    />
  );
};

/** A unique identifier for an item in a PropertyList */
export type PropertyListItemID = string;

type CheckedIndicatorProps = {
  /**
   * Renders either a radio or checkbox indicator at the beginning of the item.
   */
  checkedIndicator?: 'radio' | 'checkbox';
  /**
   * Whether the item is selected. Used together with checkedIndicator.
   */
  isSelected?: boolean;
  /**
   * Tooltip shown on hover over the checked indicator.
   */
  checkedIndicatorTooltip?: string;
};

const CheckedIndicator = ({
  checkedIndicator,
  isSelected,
  checkedIndicatorTooltip,
}: CheckedIndicatorProps) => {
  if (!checkedIndicator) return null;
  const CheckedComponent = checkedIndicator === 'radio' ? Radio : Checkbox;

  const indicator = (
    <View
      width="$4"
      gridColumnStart={2}
      pointerEvents={checkedIndicatorTooltip ? 'auto' : 'none'}
    >
      <CheckedComponent isChecked={isSelected} />
    </View>
  );

  if (checkedIndicatorTooltip) {
    return <Tooltip label={checkedIndicatorTooltip}>{indicator}</Tooltip>;
  }

  return indicator;
};

type ItemProps = CheckedIndicatorProps & {
  ['aria-controls']?: string;
  ['aria-expanded']?: boolean;
  ['aria-haspopup']?: string;
  ['aria-label']?: string;
  role?: string;
  tabIndex?: number;
  id: PropertyListItemID;
  icon?: React.ReactNode;
  label: React.ReactNode;
  isLinked?: boolean;
  deletable?: boolean;
  deleteDisabled?: boolean;
  sortable?: boolean;
  hidden?: boolean;
  hover?: boolean;
  isDragging?: boolean;
  isOver?: boolean;
  canDrop?: boolean;
  duplicable?: boolean;
  didDrop?: boolean;
  idOfItemBeingDragged?: string;
  style?: CSSProperties;
  ['data-automation-id']?: string;
  labelStyle?: CSSProperties;
  showIconsByDefault?: boolean;
  editable?: boolean;
  onEditButtonClicked?: any;
  onDeleteButtonClicked?: any;
  onClick?: null | ((arg1: React.MouseEvent<HTMLElement>) => void);
  onMouseOut?: (arg1: React.MouseEvent<HTMLElement>) => void;
  onMouseOver?: (arg1: React.MouseEvent<HTMLElement>) => void;
  onContextMenu?: (arg1: React.MouseEvent<HTMLElement>) => void;
  onBlur?: (arg1: React.FocusEvent<HTMLElement>) => void;
  onFocus?: (arg1: React.FocusEvent<HTMLElement>) => void;
  onKeyDown?: (arg1: React.KeyboardEvent<HTMLElement>) => void;
  t?: DesignerTranslationFn;
  trailingIcon?: React.ReactNode;
  _ref?: React.RefObject<any> | ((value: any) => void);

  // Determines whether or not we should use the hover state styling (regardless of hover)
  forceHoverState?: boolean;
  shouldShowUnlinkedWarning?: boolean;
};
type EnhancedItemProps = ItemProps & {
  isSelected?: boolean;
  disabled?: boolean;
  deletable?: boolean;
  sortable?: boolean;
  duplicable?: boolean;
  hidden?: boolean;
  deleteDisabled?: boolean;
  sortType: symbol | string;
  connectDragSource?: any;
  connectDropTarget?: any;
  onItemDropped?: ItemDroppedHandler;
  onDeleteButtonClicked?: any;
  onDuplicateButtonClicked?: any;
  onToggleButtonClicked?: any;
  order: PropertyListItemID[];
  theme: Theme;
  shouldShowUnlinkedWarning?: boolean;
  shouldShowTargetIcon?: boolean;
  useLargeIcons?: boolean;
};

const stopPropagationHandler =
  (handler: (event?: React.SyntheticEvent<HTMLElement>) => any) =>
  (event: React.SyntheticEvent<HTMLElement>) => {
    event.stopPropagation();
    handler && handler(event);
  };

const ActionWrapperView = emotionStyled('div')({
  display: 'grid',
  gridAutoFlow: 'column',
  justifySelf: 'end',
  gridGap: 0,
  '&:empty': {
    display: 'none !important',
  },
});

const ActionWrapper = ({children}: {children: React.ReactNode}) => (
  <ActionWrapperView data-automation-id="property-list-item-action-wrapper">
    {children}
  </ActionWrapperView>
);

class PropertyListItemBase extends React.PureComponent<EnhancedItemProps> {
  render() {
    const {
      checkedIndicator,
      checkedIndicatorTooltip,
      id,
      idOfItemBeingDragged,
      order,
      theme,
      icon,
      label,
      isSelected,
      isLinked,
      shouldShowUnlinkedWarning = false,
      shouldShowTargetIcon = false,
      useLargeIcons = false,
      t,
      hover,
      disabled,
      showIconsByDefault = false,
      deletable = true,
      duplicable = true,
      hidden = false,
      editable = false,
      deleteDisabled,
      connectDragSource,
      connectDropTarget,
      isOver,
      isDragging,
      canDrop,
      onDeleteButtonClicked,
      onDuplicateButtonClicked,
      onToggleButtonClicked,
      onEditButtonClicked,
      style: _style,
      labelStyle,
      forceHoverState,
      trailingIcon,
      _ref,
      ...props
    } = this.props;

    const isOtherDragging = typeof idOfItemBeingDragged === 'string';
    if (isDragging) {
      return (
        // @ts-expect-error - TS2322 - Type '{ style: { backgroundColor: string; width: string; height: number; boxShadow: "inset 0 1px 3px rgba(0,0,0,0.2)"; }; "aria-controls"?: string | undefined; "aria-expanded"?: boolean | undefined; ... 12 more ...; children?: ReactNode; }' is not assignable to type 'DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>'.
        <div
          {...props}
          ref={_ref}
          style={{
            backgroundColor: theme.colors.backgroundDeep,
            width: '100%',
            height: theme.control.height,
            boxShadow: `inset 0 1px 3px rgba(0,0,0,0.2)`,
          }}
        />
      );
    }

    const isDraggable = Boolean(connectDragSource && connectDropTarget);

    const gu = theme.gutters.base;

    // Define grid template columns
    let gridTemplateColumnValue: number | undefined;
    const hasSecondColumn = checkedIndicator || icon;
    if (checkedIndicator) {
      gridTemplateColumnValue = gu * 3;
    }
    if (icon) {
      gridTemplateColumnValue = gu * 4;
    }
    const dragColumn = isDraggable ? `${gu * 2}px` : '0px';
    const secondColumn = hasSecondColumn ? `${gridTemplateColumnValue}px` : '';
    const labelColumn = 'minmax(auto, min-content)';
    const contentColumn = '1fr';
    const actionsColumn = 'auto';

    const style = {
      [SpringIcon.__experimentalTokens.color]: hover
        ? tokens.colors.textPrimary
        : tokens.colors.textSecondary,
      display: 'grid',
      // prettier-ignore
      gridTemplateColumns: `${dragColumn} ${secondColumn} ${labelColumn} ${contentColumn} ${actionsColumn}`,
      gridGap: `${gu * 2}px ${gu}px`,
      height: theme.control.height,
      alignItems: 'center',
      backgroundColor:
        (hover && !isOtherDragging && !disabled) || forceHoverState
          ? theme.list.propertyItemBackgroundHover
          : theme.list.propertyItemBackground,
      paddingLeft: !isDraggable ? theme.gutters.base * 2 : undefined,
      pointerEvents: 'all',
      ...theme.typography.finePrint,
      lineHeight: theme.typography.finePrint.lineHeight + 'px',
      ...unselectable,
      position: 'relative',
      overflow: 'visible',
      ..._style,
    } as const;

    return (
      // @ts-expect-error - error TS2322 - Type '{ "aria-controls"?: string | undefined; "aria-expanded"?: boolean | undefined; "aria-haspopup"?: string | undefined; sortable?: boo...
      <div
        {...omit(props, ['sortType'])}
        style={style}
        ref={(ref) => {
          if (_ref && typeof _ref === 'function') {
            _ref(ref);
          } else if (_ref) {
            // @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
            _ref.current = ref;
          }
          if (
            !connectDragSource ||
            !connectDropTarget ||
            ref == null ||
            isDragging
          ) {
            return;
          }

          if (canDrop) {
            connectDropTarget(ref);
          } else {
            connectDragSource(ref);
          }
        }}
      >
        {isDraggable && (hover || showIconsByDefault) && (
          <GrabItemIcon size={10} />
        )}

        <CheckedIndicator
          checkedIndicator={checkedIndicator}
          isSelected={isSelected}
          checkedIndicatorTooltip={checkedIndicatorTooltip}
        />

        {icon && (
          <IconContainer isHidden={hidden} useLargeIcons={useLargeIcons}>
            {icon}
          </IconContainer>
        )}

        <LabelContainer
          style={labelStyle}
          isHidden={hidden}
          columnNumber={hasSecondColumn ? 3 : 2}
        >
          {label}
        </LabelContainer>

        <ActionWrapper>
          {(hover || showIconsByDefault) &&
            !disabled &&
            (deletable || duplicable || editable || shouldShowTargetIcon) &&
            (onDuplicateButtonClicked ||
              onDeleteButtonClicked ||
              onEditButtonClicked ||
              isDraggable ||
              shouldShowTargetIcon) &&
            !isOtherDragging && (
              <>
                {duplicable && onDuplicateButtonClicked && (
                  <IconButton
                    Icon={DuplicateOutlineIcon}
                    label="Duplicate"
                    withTooltip
                    variant="ghost"
                    density="compact"
                    onClick={stopPropagationHandler(onDuplicateButtonClicked)}
                  />
                )}

                {onToggleButtonClicked && (
                  <IconButton
                    Icon={hidden ? HideIcon : ShowIcon}
                    label={hidden ? 'Show' : 'Hide'}
                    withTooltip
                    variant="ghost"
                    density="compact"
                    onClick={stopPropagationHandler(onToggleButtonClicked)}
                  />
                )}

                {editable && onEditButtonClicked && (
                  <IconButton
                    Icon={EditIcon}
                    label="Edit"
                    withTooltip
                    variant="ghost"
                    density="compact"
                    onClick={stopPropagationHandler(onEditButtonClicked)}
                  />
                )}

                {deletable && onDeleteButtonClicked && (
                  <IconButton
                    Icon={DeleteIcon}
                    label="Remove"
                    withTooltip
                    variant="ghost"
                    density="compact"
                    isDisabled={deleteDisabled}
                    onClick={stopPropagationHandler(onDeleteButtonClicked)}
                    data-automation-id="PropertyListItemTrashButton"
                  />
                )}

                {shouldShowTargetIcon ? (
                  <IconButton
                    Icon={TargetIcon}
                    label="Select element"
                    withTooltip
                    variant="ghost"
                    tooltipProps={{
                      placement: 'bottom',
                    }}
                  />
                ) : null}
              </>
            )}

          {!isLinked && onDeleteButtonClicked && shouldShowUnlinkedWarning ? (
            <Tooltip
              label={
                <>
                  <UnlinkedWarningTooltipTopText>
                    {t &&
                      t(
                        'designer.components.props.warning-icon-tooltip-top-text'
                      )}
                  </UnlinkedWarningTooltipTopText>

                  {t &&
                    t(
                      'designer.components.props.warning-icon-tooltip-bottom-text'
                    )}
                </>
              }
              asChild
              placement="bottom-start"
            >
              <IconWrapper theme={theme} noGap>
                <WarningTriangleIcon
                  size={16}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
              </IconWrapper>
            </Tooltip>
          ) : null}
          {trailingIcon && (
            <IconWrapper theme={theme} noGap>
              {trailingIcon}
            </IconWrapper>
          )}
        </ActionWrapper>

        {!hover &&
          hidden &&
          onToggleButtonClicked &&
          deletable &&
          onDeleteButtonClicked && (
            <ActionWrapper>
              <IconButton
                Icon={HideIcon}
                label="Show"
                withTooltip
                variant="ghost"
                density="compact"
                onClick={stopPropagationHandler(onToggleButtonClicked)}
              />

              <IconButton
                Icon={DeleteIcon}
                label="Remove"
                withTooltip
                variant="ghost"
                density="compact"
                onClick={stopPropagationHandler(onDeleteButtonClicked)}
              />
            </ActionWrapper>
          )}

        {isOver && isOtherDragging && (
          <DropIndicator
            theme={theme}
            id={id}
            idOfItemBeingDragged={idOfItemBeingDragged}
            order={order}
          />
        )}
      </div>
    );
  }
}
// @ts-expect-error - TS2339 - Property 'displayName' does not exist on type 'typeof PropertyListItemBase'.
PropertyListItemBase.displayName = 'PropertyListItemBase';

const SortablePropertyListItem = makeSortable(PropertyListItemBase);

type PropertyListContextValue = {
  sortable?: boolean;
  disabled?: boolean;
  deletable?: boolean;
  duplicable?: boolean;
  sortType: symbol | string;
  order: PropertyListItemID[];
  onItemDeleted?: (id: PropertyListItemID) => void;
  onItemDuplicated?: (id: PropertyListItemID) => void;
  onItemToggled?: (id: PropertyListItemID) => void;
  onItemEdited?: (id: PropertyListItemID) => void;
  onItemDropped?: ItemDroppedHandler;
  shouldShowUnlinkedWarning?: boolean;
  shouldShowTargetIcon?: boolean;
  useLargeIcons?: boolean;
};

export const PropertyListContext =
  React.createContext<PropertyListContextValue>({
    sortType: Symbol(),
    order: [],
  });

/**
 * **[📖 View in Storybook ➡️](http://localhost:9001/?selectedKind=Property%20List)**
 */
class InternalPropertyListItem extends React.PureComponent<
  ItemProps & {
    theme: Theme;
  },
  any
> {
  static contextType = PropertyListContext;
  declare context: React.ContextType<typeof PropertyListContext>;

  state: {
    hover: boolean;
  } = {
    hover: false,
  };

  setHover = () => {
    this.setState({hover: true});
  };

  setHoverOff = () => {
    this.setState({hover: false});
  };

  onMouseEnter = this.setHover;

  onMouseMove = this.setHover;

  onMouseLeave = this.setHoverOff;

  onDeleteButtonClicked = () => {
    if (this.context.onItemDeleted) {
      this.context.onItemDeleted(this.props.id);
    }
  };

  onDuplicateButtonClicked = () => {
    if (this.context.onItemDuplicated) {
      this.context.onItemDuplicated(this.props.id);
    }
  };

  onToggleButtonClicked = () => {
    if (this.context.onItemToggled) {
      this.context.onItemToggled(this.props.id);
    }
  };

  onEditButtonClicked = () => {
    if (this.context.onItemEdited) {
      this.context.onItemEdited(this.props.id);
    }
  };

  onItemDropped: ItemDroppedHandler = (args) => {
    this.setState({hover: true}, () => {
      if (this.context.onItemDropped) {
        this.context.onItemDropped(args);
      }
    });
  };

  render() {
    const {
      duplicable,
      deletable,
      id,
      sortable = true,
      hidden = false,
    } = this.props;
    const Item =
      this.context.sortable && sortable
        ? SortablePropertyListItem
        : PropertyListItemBase;

    const handlers = {
      onMouseEnter: this.setHover,
      onMouseMove: this.setHover,
      // onMouseOver: this.setHover,
      onMouseLeave: this.setHoverOff,
      // onMouseOut: this.setHoverOff,
      // In the `this.context` checks below we are ensuring that if the context doesn't exist
      // we do not pass the corresponding handler down as props. We do this because otherwise
      // the property list items will show the buttons that would trigger those handlers.
      onDeleteButtonClicked: this.context.onItemDeleted
        ? this.onDeleteButtonClicked
        : null,
      onDuplicateButtonClicked: this.context.onItemDuplicated
        ? this.onDuplicateButtonClicked
        : null,
      onToggleButtonClicked: this.context.onItemToggled
        ? this.onToggleButtonClicked
        : null,
      onEditButtonClicked: this.context.onItemEdited
        ? this.onEditButtonClicked
        : null,
      onItemDropped: this.context.onItemDropped ? this.onItemDropped : null,
    };

    return (
      <Item
        // Allow overriding `data-automation-id` through `props`:
        data-automation-id={'PropertyListItem-' + id}
        {...this.state}
        {...handlers}
        {...this.context}
        {...this.props}
        hidden={hidden}
        deletable={deletable != null ? deletable : this.context.deletable}
        duplicable={duplicable != null ? duplicable : this.context.duplicable}
        shouldShowUnlinkedWarning={
          this.props.shouldShowUnlinkedWarning ??
          this.context.shouldShowUnlinkedWarning
        }
      />
    );
  }
}
export const PropertyListItem = (props: ItemProps) => {
  const theme = useTheme();
  const t = useTranslation();
  return <InternalPropertyListItem {...props} theme={theme} t={t} />;
};
/**
 * Called when an item is deleted.
 */
export type ItemDeletedHandler = (arg1: PropertyListItemID) => any;
export type ItemDuplicatedHandler = (arg1: PropertyListItemID) => any;
export type ItemToggledHandler = (arg1: PropertyListItemID) => any;
export type ItemEditedHandler = (arg1: PropertyListItemID) => any;

/**
 * Called when an item's sort order changes.
 */
export type ItemDroppedHandler = (arg1: {
  /** The ID of the item that was repositioned */
  itemId: PropertyListItemID;
  /** The ID of the item that the dragged item was dropped onto */
  dropTargetId: PropertyListItemID;
}) => any;

interface ListItem {
  id: PropertyListItemID;
}

type PropNoneType = {
  label?: React.ReactNode;
  style?: CSSProperties;
  _ref?: React.RefObject<any> | ((value: any) => void);
};

export const VariablePropertyListItem = styled(PropertyListItem)(({
  hover,
  isVariableBound,
}) => {
  if (!isVariableBound) {
    return {};
  }

  return {
    background: hover
      ? tokens.colors.purpleTransparentHover
      : tokens.colors.purpleTransparent,
  };
});

// prettier-ignore
export const PropertyListNone = ({
  label = 'None',
  style,
  _ref,
}: PropNoneType) => {
  const theme = useTheme();
  return (
    <PropertyListItemBase
      id="NONE"
      data-automation-id="property-list-item-none"
      icon={<div />}
      label={label}
      _ref={_ref}
      style={{
        ...style,
        color: theme.colors.foregroundDim,
        textAlign: 'center',
        gridGap: 0,
        paddingLeft: 0,
        gridTemplateColumns: '0px 0px 1fr',
      }}
      order={[]}
      sortType="None"
      theme={theme}
    />
  );
};

type Props<T> = {
  /* eslint-disable react/no-unused-prop-types */
  style?: CSSProperties;
  disabled?: boolean;
  deletable?: boolean;
  duplicable?: boolean;
  hidden?: boolean;
  onItemDeleted?: ItemDeletedHandler;
  onItemDuplicated?: ItemDuplicatedHandler;
  onItemToggled?: ItemToggledHandler;
  onItemEdited?: ItemEditedHandler;
  items: T[];
  renderItem: (arg1: T, idx: number) => React.ReactElement;
  renderNone?: () => React.ReactElement;
  renderFooter?: () => React.ReactElement | null;
  children?: undefined;
  sortable?: boolean;
  sortType?: symbol | string;
  onItemDropped?: ItemDroppedHandler;
  shouldShowUnlinkedWarning?: boolean;
  shouldShowTargetIcon?: boolean;
  useLargeIcons?: boolean;
  automationId?: string;
  /* eslint-enable react/no-unused-prop-types */
};

const PropertyListBase = <T extends ListItem>({
  items,
  renderItem,
  renderNone,
  renderFooter,
  style: _style,
  automationId,
}: Props<T>) => {
  const theme = useTheme();

  if (items.length === 0 && renderNone == null) {
    return null;
  }

  const style = {
    display: 'grid',
    alignItems: 'stretch',
    justifyItems: 'stretch',
    gridGap: theme.control.borderWidth,
    gridTemplateRows: `${theme.gutters.base * 6}`,
    gridAutoRows: `${theme.gutters.base * 6}`,
    overflow: 'hidden',
    borderRadius: theme.control.borderRadius,
    borderWidth: theme.control.borderWidth,
    borderStyle: 'solid',
    borderColor: theme.list.propertyBorderColor,
    backgroundColor: theme.list.propertyBackground,
    ..._style,
  } as const;

  return (
    <div
      style={style}
      data-automation-id={
        typeof automationId === 'string' ? automationId : 'PropertyListBase'
      }
    >
      {items.length === 0 && renderNone != null
        ? renderNone()
        : items.map(renderItem)}
      {renderFooter != null && renderFooter()}
    </div>
  );
};

const getId = ({id}: ListItem) => id;

/**
 * **[📖 View in Storybook ➡️](http://localhost:9001/?selectedKind=Property%20List)**
 */
const PropertyList = <T extends ListItem>(props: Props<T>) => {
  const {
    onItemDeleted,
    onItemDuplicated,
    onItemToggled,
    onItemEdited,
    onItemDropped,
    items,
    disabled,
    deletable,
    duplicable,
    sortable,
    sortType: externalSortType,
    shouldShowTargetIcon,
    shouldShowUnlinkedWarning,
    useLargeIcons,
  } = props;

  // Stable per-instance sortType symbol. Must not be recomputed on re-render —
  // react-dnd compares the drag source's captured sortType against the drop
  // target's current sortType via monitor.didDrop(). If the memo below
  // recomputes mid-drag (e.g. because `items` reference changes), a fresh
  // Symbol() would break that comparison and silently drop the reorder.
  const [internalSortType] = React.useState(() => Symbol());

  const propertyListContextValue: PropertyListContextValue = React.useMemo(
    () => ({
      sortable:
        (externalSortType ? items.length >= 1 : items.length > 1) &&
        (sortable || false),
      disabled,
      deletable,
      duplicable,
      sortType: externalSortType || internalSortType,
      order: items.map<PropertyListItemID>(getId),
      onItemDropped,
      onItemDeleted,
      onItemDuplicated,
      onItemToggled,
      onItemEdited,
      shouldShowUnlinkedWarning,
      shouldShowTargetIcon,
      useLargeIcons,
    }),
    [
      items,
      sortable,
      externalSortType,
      internalSortType,
      disabled,
      deletable,
      duplicable,
      onItemDropped,
      onItemDeleted,
      onItemDuplicated,
      onItemToggled,
      onItemEdited,
      shouldShowUnlinkedWarning,
      shouldShowTargetIcon,
      useLargeIcons,
    ]
  );

  return (
    <PropertyListContext.Provider value={propertyListContextValue}>
      <PropertyListBase {...props} />
    </PropertyListContext.Provider>
  );
};

export const forStorybook = {
  PropertyListBase,
  PropertyListItemBase,
} as const;

export default PropertyList;
